import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { isValidElement, useState, useMemo } from 'react';

import { isObject } from '@infinex-private/ts-utils/object';
import { ChevronDownIcon, ChevronUpIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Text } from '@infinex/ui/components';

interface ExpandableTextSectionProps {
  children: ReactNode;
  expandable?: boolean;
  title?: string;
  className?: string;
  defaultExpanded?: boolean;
  fadeGradientFrom?: string;
  lineThreshold?: number;
}

export function ExpandableTextSection({
  children,
  expandable = true,
  title,
  className,
  defaultExpanded = false,
  fadeGradientFrom = 'from-surface',
  lineThreshold = 3,
}: ExpandableTextSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || !expandable);

  // Extract text content from children to count lines
  const textContent = useMemo(() => {
    if (typeof children === 'string') {
      return children;
    }

    // Try to extract text from React elements
    const extractText = (node: ReactNode): string => {
      if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
      }
      if (isElementWithChildren(node)) {
        return extractText(node.props.children);
      }
      if (Array.isArray(node)) {
        return node.map(extractText).join('');
      }
      return '';
    };

    return extractText(children);
  }, [children]);

  // Count lines in the text content
  const lineCount = useMemo(() => {
    if (!textContent) return 0;
    return textContent.split('\n').length;
  }, [textContent]);

  // Only show expand button if expandable is true AND content exceeds line threshold
  const showExpandButton = expandable && lineCount > lineThreshold;

  return (
    <div className={className}>
      {title && (
        <div className="mb-2">
          <Text className="text-tertiary text-xs font-semibold">{title}</Text>
        </div>
      )}
      <div className="relative">
        <div
          className={cn(
            'relative overflow-hidden transition-all duration-200',
            showExpandButton && !isExpanded && 'max-h-24'
          )}
        >
          {children}
          {showExpandButton && !isExpanded && (
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent',
                fadeGradientFrom,
                fadeGradientFrom.includes('surfaceTwo')
                  ? 'via-surfaceTwo/80'
                  : 'via-surface/80'
              )}
            />
          )}
        </div>
        {showExpandButton && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="bg-fill hover:bg-fillHover absolute right-2 bottom-2 z-10 flex items-center gap-1 rounded-xl p-2 transition-all"
          >
            {isExpanded ? (
              <ChevronUpIcon size="xsmall" />
            ) : (
              <ChevronDownIcon size="xsmall" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

type ElementWithChildren = ReactElement<PropsWithChildren>;
function isElementWithChildren(node: ReactNode): node is ElementWithChildren {
  return (
    isValidElement(node) &&
    isObject(node.props) &&
    'children' in node.props &&
    node.props.children != undefined
  );
}
